
// pages/ProductDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Button, Chip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, IconButton,
    Breadcrumbs, Link, Divider, useTheme, useMediaQuery, Paper
} from '@mui/material';
import {
    ShoppingCart as ShoppingCartIcon,
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    ArrowBack as ArrowBackIcon,
    Share as ShareIcon,
    NavigateNext as NavigateNextIcon,
    LocalOffer as LocalOfferIcon,
    VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import productService from '../services/productService';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import cartService from '../services/cartService';
import '../styles/ProtectedRoutes.css'; // reuse your glass-* classes

const FALLBACK_LG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMDAgMTIwVjI4ME0xMjAgMjAwSDI4MCIgc3Ryb2tlPSIjOTk5OTk5IiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4K';

const GOLD = '#bc965e';

const REFERRAL_STORAGE_KEY = 'referralToken';

const ProductDetail = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { addToCart: addToCartContext, refreshCart } = useCart();
    const { user } = useAuth();
    const { isAuthenticated } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const API_BASE_URL = process.env.REACT_APP_API_URL.replace('/api', '');

    // Capture ?ref=TOKENNO from a shared product link and persist it for
    // attribution at checkout. Ignored if the visitor is the referrer themselves.
    useEffect(() => {
        const ref = searchParams.get('ref');
        if (!ref) return;

        const isSelfReferral = user?.tokenNo && String(user.tokenNo) === String(ref);
        if (!isSelfReferral) {
            try {
                sessionStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({
                    token: ref,
                    capturedAt: Date.now(),
                }));
            } catch (err) {
                console.error('Failed to store referral token:', err);
            }
        }

        // Strip ?ref= from the visible URL without losing it from storage
        searchParams.delete('ref');
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, user]);

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLiked, setIsLiked] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [showSizeDialog, setShowSizeDialog] = useState(false);
    const [selectedSize, setSelectedSize] = useState('');

    useEffect(() => {
        let cancelled = false;
        const fetchProduct = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await productService.getProductDetails(productId);
                if (!cancelled) {
                    if (res.success) setProduct(res.data);
                    else setError(res.message || 'Product not found');
                }
            } catch (err) {
                if (!cancelled) setError(err.message || 'Product not found');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchProduct();
        window.scrollTo(0, 0);
        return () => { cancelled = true; };
    }, [productId]);

    const getAvailableSizes = () => {
        const sizes = Array.isArray(product?.SizeDetails) && product.SizeDetails.length > 0
            ? product.SizeDetails
            : Array.isArray(product?.availableSizes) ? product.availableSizes : [];
        return sizes.filter(s => Number(s.QuantityInStock) > 0);
    };
    const availableSizes = getAvailableSizes();
    const hasSizes = availableSizes.length > 0;

    const handleAddToCartClick = () => {
        if (hasSizes) { setSelectedSize(''); setShowSizeDialog(true); }
        else handleAddToCart(null);
    };

    const handleAddToCart = async (size = null) => {
        if (isAddingToCart) return;


        setIsAddingToCart(true);
        try {
            await cartService.addToCart(product.ProductCode, 1, size);
            toast.success(`Added to cart${size ? ` (Size: ${size})` : ''}`);
            refreshCart();
            setShowSizeDialog(false);
        } catch (error) {
            toast.error(error.message || 'Failed to add to cart');
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleSizeDialogConfirm = () => {
        if (!selectedSize) { toast.error('Please select a size'); return; }
        handleAddToCart(selectedSize);
    };

    const handleShare = async () => {
        // If the sharer is a logged-in customer, attach their TokenNo so the
        // recipient's eventual purchase can be attributed back to them.
        const url = new URL(window.location.href);
        url.searchParams.delete('ref'); // don't propagate someone else's ref if this page itself was opened via a shared link
        if (user?.tokenNo) {
            url.searchParams.set('ref', user.tokenNo);
        }
        const shareUrl = url.toString();

        if (navigator.share) {
            try {
                await navigator.share({ title: product?.ProductName, url: shareUrl });
            } catch (err) { /* user cancelled — no-op */ }
        } else {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Product link copied to clipboard');
        }
    };

    const formatPrice = (price) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);

    const getTotalStock = () => {
        if (!product) return 0;
        // Prefer explicit TotalSizeStock if provided
        if (typeof product.TotalSizeStock === 'number') return product.TotalSizeStock;
        const sizes = Array.isArray(product.SizeDetails) && product.SizeDetails.length > 0
            ? product.SizeDetails
            : Array.isArray(product.availableSizes) ? product.availableSizes : [];
        if (sizes.length > 0) {
            return sizes.reduce((sum, s) => sum + (Number(s.QuantityInStock) || 0), 0);
        }
        // Fallback to QuantityInStock
        return Number(product.QuantityInStock) || 0;
    };

    const getStockStatus = () => {
        if (!product) return { status: 'unknown', text: '' };
        if (product.Category === 'Customized') return { status: 'available', text: 'Made to Order' };
        const stock = getTotalStock();
        if (stock > 10) return { status: 'in-stock', text: 'In Stock' };
        if (stock > 0) return { status: 'low-stock', text: `Only ${stock} left` };
        return { status: 'out-of-stock', text: 'Out of Stock' };
    };

    const getStockStatusColor = () => {
        if (!product) return 'default';
        if (product.Category === 'Customized') return 'primary';
        const stock = getTotalStock();
        if (stock > 10) return 'success';
        if (stock > 0) return 'warning';
        return 'error';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                <CircularProgress sx={{ color: GOLD }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Loading product...</Typography>
            </Box>
        );
    }

    if (error || !product) {
        return (
            <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>Product not found</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>{error}</Typography>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/products')}
                    sx={{ borderColor: GOLD, color: GOLD, '&:hover': { borderColor: GOLD, bgcolor: 'rgba(188,150,94,0.1)' } }}
                >
                    Back to Products
                </Button>
            </Box>
        );
    }

    const stockStatus = getStockStatus();
    const photoUrl = product.Photo ? `${API_BASE_URL}${product.Photo}` : FALLBACK_LG;// VERIFY: swap for env-based base URL before deploying off localhost

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, pb: { xs: 12, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Breadcrumb */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />}
                sx={{ mb: 3, display: { xs: 'none', sm: 'flex' } }}
            >
                <Link component={RouterLink} to="/" underline="hover" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                    Home
                </Link>
                <Link component={RouterLink} to="/products" underline="hover" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                    Products
                </Link>
                <Typography sx={{ color: GOLD, fontSize: '0.875rem', fontWeight: 500 }}>
                    {product.ProductName}
                </Typography>
            </Breadcrumbs>

            {/* Mobile back button */}
            <IconButton
                onClick={() => navigate(-1)}
                sx={{ mb: 1, display: { xs: 'inline-flex', sm: 'none' }, color: GOLD }}
            >
                <ArrowBackIcon />
            </IconButton>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 5 } }}>
                {/* ── Image column ── */}
                <Box sx={{ flex: { md: '0 0 46%' }, maxWidth: { md: '46%' }, position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'flex-start' }}>
                    <Paper
                        elevation={0}
                        className="glass-card"
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            border: '1px solid rgba(188,150,94,0.15)',
                        }}
                    >
                        <Box
                            component="img"
                            src={photoUrl}
                            alt={product.ProductName}
                            sx={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                objectFit: 'cover',
                                backgroundColor: 'rgba(255,255,255,0.03)',
                                display: 'block',
                            }}
                            onError={(e) => { if (e.target.src !== FALLBACK_LG) e.target.src = FALLBACK_LG; }}
                        />
                        {product.Category === 'Customized' && (
                            <Chip
                                icon={<LocalOfferIcon sx={{ color: '#000 !important' }} />}
                                label={product.Category}
                                size="small"
                                sx={{ position: 'absolute', top: 12, left: 12, bgcolor: GOLD, color: '#000', fontWeight: 700 }}
                            />
                        )}
                    </Paper>
                </Box>

                {/* ── Details column ── */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: GOLD, letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}
                            >
                                {product.ProductTypeName || product.ProductType}
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.25,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {product.ProductName}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleShare}
                            sx={{
                                flexShrink: 0,
                                border: '1px solid rgba(188,150,94,0.3)',
                                '&:hover': { bgcolor: 'rgba(188,150,94,0.1)' },
                            }}
                        >
                            <ShareIcon sx={{ color: GOLD }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h5" sx={{ color: GOLD, fontWeight: 700 }}>
                            {formatPrice(product.Price)}
                        </Typography>
                        <Chip label={stockStatus.text} color={getStockStatusColor()} size="small" sx={{ fontWeight: 600 }} />
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                    {product.Color && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 70 }}>Color</Typography>
                            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>{product.Color}</Typography>
                        </Box>
                    )}

                    {product.SizeDetails && product.SizeDetails.length > 0 && (
                        <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textAlign: "left", color: "#ffffff" }}>Available Sizes</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {product.SizeDetails.map((s, i) => (
                                    <Chip
                                        key={i}
                                        label={`${s.Size} · ${s.QuantityInStock} pcs`}
                                        size="small"
                                        variant={s.QuantityInStock > 0 ? 'filled' : 'outlined'}
                                        sx={{
                                            bgcolor: s.QuantityInStock > 0 ? 'rgba(188,150,94,0.15)' : 'transparent',
                                            color: s.QuantityInStock > 0 ? GOLD : 'rgba(255,255,255,0.35)',
                                            border: `1px solid ${s.QuantityInStock > 0 ? 'rgba(188,150,94,0.4)' : 'rgba(255,255,255,0.15)'}`,
                                            fontWeight: 500,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {product.Description && (
                        <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}>Description</Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7 }}>
                                {product.Description}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'rgba(255,255,255,0.45)' }}>
                        <VerifiedUserIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption">Authentic product, quality checked before dispatch</Typography>
                    </Box>

                    {/* Desktop action row */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, mt: 2 }}>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleAddToCartClick}
                            disabled={stockStatus.status === 'out-of-stock' || isAddingToCart}
                            startIcon={<ShoppingCartIcon />}
                            sx={{
                                flexGrow: 1,
                                background: "#a88752 !important",
                                color: '#000',
                                fontWeight: 700,
                                '&:hover': { bgcolor: '#a9824f' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            {isAddingToCart ? 'Adding...' : stockStatus.status === 'out-of-stock' ? 'Out of Stock' : 'Add to Cart'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Sticky mobile action bar */}
            <Paper
                elevation={4}
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    position: 'fixed',
                    bottom: 56, // sits above your bottom nav
                    left: 0,
                    right: 0,
                    zIndex: 1050,
                    p: 1.5,
                    gap: 1.5,
                    bgcolor: 'rgba(20,20,20,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid rgba(188,150,94,0.2)',
                }}
            >


                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddToCartClick}
                    disabled={stockStatus.status === 'out-of-stock' || isAddingToCart}
                    startIcon={<ShoppingCartIcon />}
                    sx={{
                        bgcolor: GOLD,
                        color: '#000',
                        fontWeight: 700,
                        '&:hover': { bgcolor: '#a9824f' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
                    }}
                >
                    {isAddingToCart ? 'Adding...' : stockStatus.status === 'out-of-stock' ? 'Out of Stock' : `Add to Cart · ${formatPrice(product.Price)}`}
                </Button>
            </Paper>

            {/* Size dialog */}
            <Dialog
                open={showSizeDialog}
                onClose={() => !isAddingToCart && setShowSizeDialog(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { sx: { bgcolor: '#1a1a1a', border: '1px solid rgba(188,150,94,0.2)' } } }}
            >
                <DialogTitle sx={{ color: '#fff' }}>Select Size</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>{product.ProductName}</Typography>
                    <FormControl fullWidth>
                        <InputLabel sx={{ color: 'rgba(255,255,255,0.6)' }}>Size</InputLabel>
                        <Select
                            value={selectedSize}
                            onChange={(e) => setSelectedSize(e.target.value)}
                            label="Size"
                            sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188,150,94,0.3)' } }}
                        >
                            {availableSizes.map((s) => (
                                <MenuItem key={s.Size} value={s.Size}>{s.Size} ({s.QuantityInStock} available)</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setShowSizeDialog(false)} disabled={isAddingToCart} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSizeDialogConfirm}
                        disabled={isAddingToCart || !selectedSize}
                        sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#a9824f' } }}
                    >
                        {isAddingToCart ? 'Adding...' : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductDetail;